// @ts-check
/* ═══════════════════════ SOLTIDER (#8) ═══════════════════════
   Soloppgang/-nedgang, gyllen time, blå time og «ekte mørke» for
   aurora — beregnet lokalt fra koordinater + dato. Ingen API, så
   dette fungerer 100 % offline.

   Algoritmen er NOAAs forenklede solposisjonsmodell (samme som
   SunCalc bygger på). Presisjon: ±2 min, mer enn nok for reisebruk.

   Alaska/Yukon i juli har «midnattssol»-aktig lys: i Skagway går
   solen ned ~22:30 og opp ~04:15, og det blir aldri ekte mørkt
   (sola kommer ikke langt nok under horisonten). Vi flagger dette
   eksplisitt fordi det avgjør om aurora er mulig i det hele tatt.
   ═══════════════════════════════════════════════════════════════ */

import { DAY_COORDS } from './weather.js';

const RAD = Math.PI / 180;
const DAY_MS = 86400000;
// Julian date for Unix-epoke (1970-01-01) og J2000.0-referansen
const J1970 = 2440588;
const J2000 = 2451545;

function toJulian(date){ return date.valueOf() / DAY_MS - 0.5 + J1970; }
function toDays(date){ return toJulian(date) - J2000; }

// Solens posisjon (forenklet)
function solarMeanAnomaly(d){ return RAD * (357.5291 + 0.98560028 * d); }
function eclipticLongitude(M){
  const C = RAD * (1.9148 * Math.sin(M) + 0.02 * Math.sin(2 * M) + 0.0003 * Math.sin(3 * M));
  const P = RAD * 102.9372; // perihelion
  return M + C + P + Math.PI;
}
function declination(L){
  const e = RAD * 23.4397; // jordens aksehelning
  return Math.asin(Math.sin(e) * Math.sin(L));
}

const J0 = 0.0009;
function julianCycle(d, lw){ return Math.round(d - J0 - lw / (2 * Math.PI)); }
function approxTransit(Ht, lw, n){ return J0 + (Ht + lw) / (2 * Math.PI) + n; }
function solarTransitJ(ds, M, L){ return J2000 + ds + 0.0053 * Math.sin(M) - 0.0069 * Math.sin(2 * L); }
function hourAngle(h, phi, dec){
  const cosH = (Math.sin(h) - Math.sin(phi) * Math.sin(dec)) / (Math.cos(phi) * Math.cos(dec));
  return Math.acos(Math.max(-1, Math.min(1, cosH)));
}

function fromJulian(j){
  if (!isFinite(j)) return null;
  return new Date((j + 0.5 - J1970) * DAY_MS);
}

/**
 * Tidspunktet (Date) for en gitt solhøyde `h` (radianer) ved opp- eller nedgang.
 * @returns {Date|null} null = solen krysser aldri denne høyden (polar dag/natt)
 */
function timeForAngle(h, rising, date, lat, lng){
  const lw = RAD * -lng;
  const phi = RAD * lat;
  const d = toDays(date);
  const n = julianCycle(d, lw);
  const ds = approxTransit(0, lw, n);
  const M = solarMeanAnomaly(ds);
  const L = eclipticLongitude(M);
  const dec = declination(L);

  const cosH = (Math.sin(h) - Math.sin(phi) * Math.sin(dec)) / (Math.cos(phi) * Math.cos(dec));
  if (cosH > 1) return null;   // solen kommer aldri så høyt (polar natt-aktig)
  if (cosH < -1) return null;  // solen går aldri så lavt (midnattssol)

  const w = hourAngle(h, phi, dec);
  const a = approxTransit(rising ? -w : w, lw, n);
  return fromJulian(solarTransitJ(a, M, L));
}

const ANGLES = {
  sunrise:   -0.833 * RAD, // sol berører horisonten (refraksjon medregnet)
  goldenEnd:  6 * RAD,     // gyllen time: sol 0–6° over horisonten
  blueEnd:   -4 * RAD,     // blå time: sol 0 til -4°
  civil:     -6 * RAD,     // borgerlig skumring
  astro:    -18 * RAD,     // astronomisk natt — under dette er det «ekte mørkt»
};

function fmt(date, tz){
  if (!date) return null;
  try {
    return new Intl.DateTimeFormat('nb-NO', {
      hour: '2-digit', minute: '2-digit', hour12: false, timeZone: tz,
    }).format(date);
  } catch {
    return date.toISOString().slice(11, 16);
  }
}

/**
 * Full sol-rapport for et sted og en dato.
 * @param {number} lat
 * @param {number} lng
 * @param {Date} date
 * @param {string} tz  IANA-tidssone for visning (f.eks. 'America/Vancouver')
 */
export function sunTimes(lat, lng, date = new Date(), tz = 'America/Vancouver'){
  const noon = new Date(date); noon.setUTCHours(12, 0, 0, 0);
  const rise   = timeForAngle(ANGLES.sunrise, true, noon, lat, lng);
  const set    = timeForAngle(ANGLES.sunrise, false, noon, lat, lng);
  const goldenRiseEnd = timeForAngle(ANGLES.goldenEnd, true, noon, lat, lng);
  const goldenSetStart = timeForAngle(ANGLES.goldenEnd, false, noon, lat, lng);
  const duskAstro = timeForAngle(ANGLES.astro, false, noon, lat, lng);
  const dawnAstro = timeForAngle(ANGLES.astro, true, noon, lat, lng);

  // «Ekte mørke» finnes bare hvis solen faktisk når under -18°
  const trueDark = !!(duskAstro && dawnAstro);
  // Midnattssol-aktig: solen går aldri ned
  const midnightSun = !set && !rise;

  // Dagslengde i minutter
  let dayLengthMin = null;
  if (rise && set) dayLengthMin = Math.round((set - rise) / 60000);
  else if (midnightSun) dayLengthMin = 24 * 60;

  return {
    rise, set,
    riseStr: fmt(rise, tz), setStr: fmt(set, tz),
    goldenMorningEnd: fmt(goldenRiseEnd, tz),
    goldenEveningStart: fmt(goldenSetStart, tz),
    duskAstroStr: fmt(duskAstro, tz),
    dawnAstroStr: fmt(dawnAstro, tz),
    trueDark, midnightSun, dayLengthMin, tz,
  };
}

// IANA-tidssone per reisedag (for korrekt lokal klokke)
const DAY_TZ = [
  'America/Vancouver', 'America/Vancouver', 'America/Vancouver', // 0–2
  'America/Juneau', 'America/Juneau', 'America/Juneau', 'America/Juneau', 'America/Juneau', // 3–7 Alaska
  'America/Vancouver', // 8 Inside Passage (BC-side)
  'America/Vancouver', // 9 Kamloops
  'America/Edmonton', 'America/Edmonton', 'America/Edmonton', 'America/Edmonton', // 10–13 Jasper/Banff
  'America/Edmonton', 'America/Edmonton', 'America/Edmonton', // 14–16 Calgary
  'Atlantic/Reykjavik', // 17
];

const TRIP_START = new Date('2026-07-17T00:00:00+02:00');

function dateForTripDay(tripDay){
  const d = new Date(TRIP_START);
  d.setDate(d.getDate() + tripDay);
  return d;
}

/**
 * Sol-rapport for en reisedag (0..17). Bruker DAY_COORDS + riktig tidssone.
 * Returnerer null hvis dagen er ukjent.
 */
export function sunForTripDay(tripDay){
  const c = DAY_COORDS[tripDay];
  if (!c) return null;
  const tz = DAY_TZ[tripDay] || 'America/Vancouver';
  const out = sunTimes(c.lat, c.lng, dateForTripDay(tripDay), tz);
  out.loc = c.loc;
  return out;
}

/** Kort badge: «↑ 04:18 ↓ 22:31 · 18t 13m». */
export function formatSunBadge(s){
  if (!s) return '';
  if (s.midnightSun) return '☀ Midnattssol — solen går ikke ned';
  if (!s.riseStr || !s.setStr) return '';
  const len = s.dayLengthMin != null
    ? ` · ${Math.floor(s.dayLengthMin / 60)}t ${s.dayLengthMin % 60}m`
    : '';
  return `↑ ${s.riseStr} ↓ ${s.setStr}${len}`;
}
